import { createPrimaryUser } from '@/api/nextme/registration'
import InputField from '@/components/common/fields/InputField'
import CheckboxField from '@/components/common/fields/CheckboxField'
import { Form, Formik } from 'formik'
import { useState } from 'react'
import styles from '@/styles/RegistrationForm.module.scss'
import formStyles from '@/styles/Forms.module.scss'
import buttonStyles from '@/styles/Buttons.module.scss'
import typography from '@/styles/Typography.module.scss'
import colors from '@/styles/Colors.module.scss'
import classNames from 'classnames'
import * as Yup from 'yup'
import Link from 'next/link'
import PasswordInput from '@/components/common/forms/formik/PasswordInput'
import EmailVerifyModal from '@/components/pages/signup/EmailVerifyModal'
import { printAxiosErrors } from '@/utils/util-functions'
import { useDispatch } from 'react-redux'
import { setAlert } from '@/store/slices/alertSlice'
import { getCSRFToken, postLogin } from '@/api/nextme/login'
import useAnalytics from '@/hooks/useAnalytics'
import { UserRoleName } from '@/consts/UserRolesEnum'
import { dayjs } from '@/utils/dayjs'
import { HelperText } from '@/components/common/forms/Common'

interface FormValues {
  email: string
  password: string
  passwordConfirmation: string
  firstName: string
  lastName: string
  agreeToc: boolean
  status: 'enabled' | 'disabled'
}

const TOCLabel = () => {
  return (
    <div className={formStyles.checkboxLabel}>
      <span>I agree to NextMe's </span>
      <Link href="https://nextmeapp.com/terms-of-use/">
        <a target="_blank">Terms of Use</a>
      </Link>
      <span> and </span>
      <Link href="https://nextmeapp.com/privacy-policy/">
        <a target="_blank">Privacy Policy</a>
      </Link>
    </div>
  )
}

export default function SignupForm(_props) {
  const dispatch = useDispatch()
  const { analytics } = useAnalytics(true)
  const [showEmailVerifyModal, setShowEmailVerifyModal] = useState(false)
  const [TOCCheckboxError, setTOCCheckboxError] = useState(false)
  const [emailDuplicateError, setEmailDuplicateError] = useState('')
  const [passwordErrors, setPasswordErrors] = useState([])

  const initialValues: FormValues = {
    email: '',
    password: '',
    passwordConfirmation: '',
    firstName: '',
    lastName: '',
    agreeToc: false,
    status: 'enabled', // TODO: yikes
  }

  const validationSchema = Yup.object({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    password: Yup.string().min(12).required(' '),
    passwordConfirmation: Yup.string()
      .test(
        'passwordConfirmation',
        ({ value }) => (value?.length >= 12 ? 'passwords must match' : ' '),
        (val, context) => val === context.parent.password
      )
      .required(' '),
    email: Yup.string().email().required('Required'),
  })

  const onSubmit = async (values: FormValues) => {
    setTOCCheckboxError(false)

    if (values.agreeToc !== true) {
      setTOCCheckboxError(true)
      return
    }

    await getCSRFToken().then(async () => {
      await createPrimaryUser(values)
        .then(async (res) => {
          analytics.track({ event: 'account_created', properties: {} })
          await postLogin({
            email: values.email,
            password: values.password,
            remember: true,
          })

          setShowEmailVerifyModal(true)
        })
        .catch((err) => {
          console.error(printAxiosErrors(err))

          const { errors } = err?.response?.data || {}
          let alertTitle = err.response?.data?.message
          let error = ''

          for (const e in errors) {
            if (
              e === 'email' &&
              errors[e].some((x: string) => x.includes('already'))
            ) {
              setEmailDuplicateError('Email has already been registered')
              alertTitle = 'Email has already been registered'
            }
            if (e === 'password') {
              error = 'password'
              setPasswordErrors(errors[e])
            }
          }
          if (error !== 'password') {
            dispatch(
              setAlert({
                isOpen: true,
                title: alertTitle,
                type: 'error',
                message: printAxiosErrors(err, true),
              })
            )
          }
        })
    })
  }

  return (
    <div className={styles.form}>
      <h5 className={typography.h5}>Create New Account</h5>

      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnChange={false}
        noValidate
      >
        {({ setFieldValue, isSubmitting, values }) => (
          <Form style={{ marginTop: '1rem' }}>
            <InputField
              label="First Name"
              name="firstName"
              hideError
              required
            />
            <InputField label="Last Name" name="lastName" hideError required />
            <InputField
              autoComplete="username"
              label="Email"
              name="email"
              hideErrorEmpty
              error={emailDuplicateError}
              onChange={(e) => {
                setEmailDuplicateError('')
                setFieldValue('email', e.target.value)
              }}
              required
            />
            <PasswordInput
              autoComplete="new-password"
              name="password"
              label="Password"
              required
              hideErrorEmpty
              notes="Passwords must be at least 12 characters long and contain lower and uppercase letters, a number, and a symbol."
              error={passwordErrors}
            />
            <PasswordInput
              name="passwordConfirmation"
              label="Confirm Password"
              required
              hideErrorEmpty
            />
            <CheckboxField
              name="agreeToc"
              options={[{ component: <TOCLabel />, value: true }]}
            />
            {TOCCheckboxError && (
              <HelperText hasError={true}>
                {TOCCheckboxError &&
                  'Please agree to the terms and privacy policy.'}
              </HelperText>
            )}

            <button
              className={classNames('btn w-100', buttonStyles.enabledGreenBtn)}
              style={{ marginTop: '0.75rem' }}
              type="submit"
              onClick={(e) => isSubmitting && e.preventDefault()}
              disabled={isSubmitting}
            >
              Sign{isSubmitting ? `ing Up...` : ' Up'}
            </button>

            <EmailVerifyModal
              open={showEmailVerifyModal}
              email={values.email}
            />
          </Form>
        )}
      </Formik>
    </div>
  )
}
